import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useGetPropertiesQuery } from '../../features/api/apiSlice';
import useDebounce from './useDebounce';
import { isLuxuryListing } from '../propertyLabels';
import { trackEvent } from '../analytics';

const usePropertySearch = (initialFilters, itemsPerPage = 16) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(initialFilters);

  // Initialize from URL params
  useEffect(() => {
    const urlFilters = {
      location: searchParams?.get('location') || '',
      transactionType: searchParams?.get('type') || 'all',
      propertyType: searchParams?.get('category_id') || 'all',
      priceRange: [
        parseInt(searchParams?.get('priceRangeMin')) || initialFilters.priceRange[0],
        parseInt(searchParams?.get('priceRangeMax')) || initialFilters.priceRange[1]
      ],
      bedrooms: searchParams?.get('bedrooms') || 'any',
      bathrooms: searchParams?.get('bathrooms') || 'any',
      sqftRange: [
        parseInt(searchParams?.get('sqftRangeMin')) || initialFilters.sqftRange[0],
        parseInt(searchParams?.get('sqftRangeMax')) || initialFilters.sqftRange[1]
      ],
      yearBuiltRange: [
        parseInt(searchParams?.get('yearBuiltMin')) || initialFilters.yearBuiltRange[0],
        parseInt(searchParams?.get('yearBuiltMax')) || initialFilters.yearBuiltRange[1]
      ],
      status: searchParams?.get('status') || 'all',
      sortBy: searchParams?.get('sort') || 'relevance',
      amenities: searchParams?.get('amenities')?.split(',')?.filter(Boolean) || [],
      features: searchParams?.get('features')?.split(',')?.filter(Boolean) || [],
      recentlyAdded: searchParams?.get('recentlyAdded') || 'any',
      hasVirtualTour: searchParams?.get('hasVirtualTour') === 'true',
      distanceFromCity: parseInt(searchParams?.get('distanceFromCity')) || initialFilters.distanceFromCity,
      hoaFeeMax: searchParams?.get('hoaFeeMax') || '',
      lotSizeMin: searchParams?.get('lotSizeMin') || '',
      // Add dynamic category parameters
      ...Object.fromEntries(
        Array.from(searchParams.entries())
          .filter(([key]) => key.startsWith('param_'))
          .map(([key, value]) => [key, value.includes(',') ? value.split(',') : value])
      )
    };

    setFilters(urlFilters);
    setSearchQuery(searchParams?.get('q') || '');
    const urlPage = parseInt(searchParams?.get('page')) || 1;
    setPage(urlPage);
  }, [searchParams, initialFilters]);

  useEffect(() => {
    const q = String(debouncedSearchQuery || '').trim();
    const location = String(filters?.location || '').trim();
    const term = q || location;
    if (!term) return;
    trackEvent(
      'property_search',
      {
        search_query: term,
        query: q || undefined,
        location: location || undefined,
        type: filters?.transactionType || undefined,
      },
      'search'
    );
  }, [debouncedSearchQuery, filters.location, filters.transactionType]);

  const updateUrlParams = useCallback((newFilters, newSearchQuery, newPage = page, newViewMode) => {
    const params = new URLSearchParams();

    if (newSearchQuery) params?.set('q', newSearchQuery);
    if (newFilters?.location) params?.set('location', newFilters?.location);
    if (newFilters?.transactionType !== 'all') {
      params?.set('type', newFilters?.transactionType);
      const propertyTypeMapping = newFilters.transactionType === 'sell' ? 0 : newFilters.transactionType === 'rent' ? 1 : undefined;
      if (typeof propertyTypeMapping === 'number') {
        params?.set('property_type', propertyTypeMapping);
      }
      if (newFilters.transactionType === 'luxury') {
        params?.set('is_luxury', '1');
      }
    }
    if (newFilters?.propertyType !== 'all') {
      params?.set('category_id', newFilters?.propertyType);
    }
    
    if (newFilters?.priceRange && (newFilters?.priceRange?.[0] !== initialFilters.priceRange[0] || newFilters?.priceRange?.[1] !== initialFilters.priceRange[1])) {
      params?.set('priceRangeMin', newFilters?.priceRange?.[0]);
      params?.set('priceRangeMax', newFilters?.priceRange?.[1]);
    }

    if (newFilters?.sqftRange && (newFilters?.sqftRange?.[0] !== initialFilters.sqftRange[0] || newFilters?.sqftRange?.[1] !== initialFilters.sqftRange[1])) {
      params?.set('sqftRangeMin', newFilters?.sqftRange?.[0]);
      params?.set('sqftRangeMax', newFilters?.sqftRange?.[1]);
    }

    if (newFilters?.yearBuiltRange && (newFilters?.yearBuiltRange?.[0] !== initialFilters.yearBuiltRange[0] || newFilters?.yearBuiltRange?.[1] !== initialFilters.yearBuiltRange[1])) {
      params?.set('yearBuiltMin', newFilters?.yearBuiltRange?.[0]);
      params?.set('yearBuiltMax', newFilters?.yearBuiltRange?.[1]);
    }

    if (newFilters?.bedrooms !== 'any') params?.set('bedrooms', newFilters?.bedrooms);
    if (newFilters?.bathrooms !== 'any') params?.set('bathrooms', newFilters?.bathrooms);
    if (newFilters?.status !== 'all') params?.set('status', newFilters?.status);
    if (newFilters?.sortBy !== 'relevance') params?.set('sort', newFilters?.sortBy);

    if (newFilters?.amenities && newFilters?.amenities?.length > 0) {
      params?.set('amenities', newFilters?.amenities?.join(','));
    }
    if (newFilters?.features && newFilters?.features?.length > 0) {
      params?.set('features', newFilters?.features?.join(','));
    }
    if (newFilters?.recentlyAdded !== 'any') params?.set('recentlyAdded', newFilters?.recentlyAdded);
    if (newFilters?.hasVirtualTour) params?.set('hasVirtualTour', 'true');
    if (newFilters?.distanceFromCity !== initialFilters.distanceFromCity) params?.set('distanceFromCity', newFilters?.distanceFromCity);
    if (newFilters?.hoaFeeMax) params?.set('hoaFeeMax', newFilters?.hoaFeeMax);
    if (newFilters?.lotSizeMin) params?.set('lotSizeMin', newFilters?.lotSizeMin);
    
    // Add dynamic category parameters to URL
    Object.keys(newFilters).forEach(key => {
      if (key.startsWith('param_') && newFilters[key]) {
        params.set(key, Array.isArray(newFilters[key]) ? newFilters[key].join(',') : newFilters[key]);
      }
    });

    if (newPage > 1) params?.set('page', newPage?.toString());
    if (newViewMode) params?.set('view', newViewMode);

    setSearchParams(params);
  }, [setSearchParams, page, initialFilters]);

  const propertyTypeMapping = filters.transactionType === 'sell' ? 0 : filters.transactionType === 'rent' ? 1 : undefined;
  const luxuryMode = filters.transactionType === 'luxury';

  const queryParams = {
    q: debouncedSearchQuery,
    sort: filters.sortBy,
    type: filters.transactionType !== 'all' && !luxuryMode ? filters.transactionType : undefined,
    property_type: luxuryMode ? undefined : (typeof propertyTypeMapping === 'number' ? propertyTypeMapping : undefined),
    is_luxury: luxuryMode ? 1 : undefined,
    category_id: filters.propertyType !== 'all' ? filters.propertyType : undefined,
    min_price: filters.priceRange[0],
    max_price: filters.priceRange[1] === initialFilters.priceRange[1] ? undefined : filters.priceRange[1],
    bedrooms: filters.bedrooms !== 'any' ? filters.bedrooms : undefined,
    bathrooms: filters.bathrooms !== 'any' ? filters.bathrooms : undefined,
    min_area: filters.sqftRange[0] > 0 ? filters.sqftRange[0] : undefined,
    max_area: filters.sqftRange[1] === initialFilters.sqftRange[1] ? undefined : filters.sqftRange[1],
    city: filters.location || undefined, // Map frontend 'location' to backend 'city'
    status: filters.status !== 'all' ? filters.status : undefined,
    amenities: filters.amenities?.length > 0 ? filters.amenities?.join(',') : undefined,
    features: filters.features?.length > 0 ? filters.features?.join(',') : undefined,
    recently_added: filters.recentlyAdded !== 'any' ? filters.recentlyAdded : undefined,
    has_virtual_tour: filters.hasVirtualTour ? true : undefined,
    distance_from_city: filters.distanceFromCity !== initialFilters.distanceFromCity ? filters.distanceFromCity : undefined,
    hoa_fee_max: filters.hoaFeeMax || undefined,
    lot_size_min: filters.lotSizeMin || undefined,
    // Add dynamic category parameters to queryParams
    ...Object.fromEntries(
      Object.entries(filters)
        .filter(([key]) => key.startsWith('param_'))
        .map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : value])
    ),
    offset: (page - 1) * itemsPerPage,
    limit: itemsPerPage,
  };

  // Remove undefined values
  Object.keys(queryParams).forEach(key => {
    if (queryParams[key] === undefined || queryParams[key] === '' || (Array.isArray(queryParams[key]) && queryParams[key].length === 0)) {
      delete queryParams[key];
    }
  });

  console.log('Final Query Parameters being sent to API:', JSON.stringify(queryParams, null, 2)); // Debugging line

  const {
    data: { data: fetchedProperties = [], total_count: fetchedTotal = 0 } = {},
    isLoading,
    isFetching,
    isError,
    error,
    refetch
  } = useGetPropertiesQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const properties = useMemo(() => {
    if (!luxuryMode) return fetchedProperties;
    return fetchedProperties.filter(isLuxuryListing);
  }, [fetchedProperties, luxuryMode]);

  const totalResults = luxuryMode ? properties.length : fetchedTotal;
  const totalPages = Math.ceil((totalResults || 0) / itemsPerPage);

  const handleFiltersChange = (newFilters) => {
    console.log('New filters received in usePropertySearch:', JSON.stringify(newFilters, null, 2)); // Debugging line
    setFilters(newFilters);
    setPage(1); // Reset page to 1 on filter change
    updateUrlParams(newFilters, searchQuery, 1); // Pass page 1
  };

  const handleFilterRemove = (key, value) => {
    let newFilters = { ...filters };

    if (key === 'priceRange') {
      newFilters.priceRange = initialFilters.priceRange;
    } else if (key === 'sqftRange') {
      newFilters.sqftRange = initialFilters.sqftRange;
    } else if (key === 'yearBuiltRange') {
      newFilters.yearBuiltRange = initialFilters.yearBuiltRange;
    } else if (key === 'amenities' || key === 'features' || key.startsWith('param_') && Array.isArray(newFilters[key])) {
      newFilters[key] = newFilters[key]?.filter(item => item !== value);
    } else if (key === 'hasVirtualTour') {
      newFilters.hasVirtualTour = false;
    } else if (key === 'distanceFromCity') {
      newFilters.distanceFromCity = initialFilters.distanceFromCity;
    } else if (key === 'hoaFeeMax' || key === 'lotSizeMin') {
      newFilters[key] = '';
    } else {
      const defaultValue = initialFilters[key] !== undefined ? initialFilters[key] : '';
      newFilters[key] = defaultValue;
    }
    
    setFilters(newFilters);
    setPage(1); // Reset page to 1 on filter removal
    updateUrlParams(newFilters, searchQuery, 1); // Pass page 1
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setSearchQuery('');
    setPage(1); // Reset page to 1 on clear filters
    updateUrlParams(initialFilters, '', 1); // Pass page 1
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setPage(1); // Reset page to 1 on search change
    updateUrlParams(filters, query, 1); // Pass page 1
  };

  const handleSortChange = (sortBy) => {
    const newFilters = { ...filters, sortBy };
    setFilters(newFilters);
    updateUrlParams(newFilters, searchQuery);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    updateUrlParams(filters, searchQuery, newPage);
  };

  return {
    properties,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    page,
    setPage,
    filters,
    setFilters,
    queryParams,
    totalPages,
    totalResults,
    updateUrlParams,
    handleFiltersChange,
    handleFilterRemove,
    handleClearFilters,
    handleSearchChange,
    handleSortChange,
    handlePageChange,
  };
};

export default usePropertySearch;
